import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormLabel,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Radio,
  Slider,
  Switch,
  TextField,
  Typography,
  Card,
  CardContent,
} from "@mui/material";
import QuizIcon from "@mui/icons-material/Quiz";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import Swal from "sweetalert2";
import { createPregunta, updatePregunta } from "../services/preguntasService";

const getInitialPreguntaForm = (initialPregunta) => {
  if (!initialPregunta) {
    return {
      pregunta: "",
      descripcion: "",
      tipo: "multiple",
      opciones: [
        { respuesta: "", correcta: false },
        { respuesta: "", correcta: false },
      ],
      valorVerdaderoFalso: true,
      respuestasAutocompletar: [""],
      rango: [0, 100],
    };
  }

  const opciones = Array.isArray(initialPregunta.opciones) ? initialPregunta.opciones : [];
  const tipo = initialPregunta.tipo || "multiple";
  return {
    pregunta: initialPregunta.pregunta || "",
    descripcion: initialPregunta.descripcion || "",
    tipo,
    opciones:
      tipo === "única" || tipo === "multiple"
        ? opciones.length >= 2
          ? opciones
          : [
              { respuesta: "", correcta: false },
              { respuesta: "", correcta: false },
            ]
        : [],
    valorVerdaderoFalso:
      tipo === "falso_verdadero"
        ? !!opciones.find((op) => op.respuesta === "Verdadero" && op.correcta)
        : true,
    respuestasAutocompletar:
      tipo === "autocompletar" && opciones.length > 0 ? opciones.map((op) => op.respuesta || "") : [""],
    rango: Array.isArray(initialPregunta.rango) ? initialPregunta.rango : [0, 100],
  };
};

const AddCourseQuestionsForm = ({ idCurso, nombreCurso, onSuccess, mode = "batch", initialPregunta = null, onCancel }) => {
  const isSingleMode = mode === "single";
  const [loadingGuardar, setLoadingGuardar] = useState(false);
  const [preguntasCurso, setPreguntasCurso] = useState([]);
  const [preguntaForm, setPreguntaForm] = useState(getInitialPreguntaForm(initialPregunta));

  const resetPreguntaForm = () => {
    setPreguntaForm(getInitialPreguntaForm(initialPregunta));
  };

  useEffect(() => {
    setPreguntaForm(getInitialPreguntaForm(initialPregunta));
  }, [initialPregunta, mode]);

  const showAlert = (title, text, icon = "info") =>
    Swal.fire({
      title,
      text,
      icon,
      target: document.body,
      confirmButtonColor: "#113253",
      didOpen: () => {
        const container = Swal.getContainer();
        const popup = Swal.getPopup();
        if (container) container.style.zIndex = "2500";
        if (popup) popup.style.zIndex = "2501";
      },
    });

  const handlePreguntaFieldChange = (e) => {
    const { name, value } = e.target;
    if (name === "tipo") {
      if (value === "autocompletar") {
        setPreguntaForm((prev) => ({
          ...prev,
          tipo: value,
          opciones: [],
          respuestasAutocompletar: [""],
        }));
        return;
      }
      if (value === "falso_verdadero") {
        setPreguntaForm((prev) => ({ ...prev, tipo: value, opciones: [] }));
        return;
      }
      setPreguntaForm((prev) => ({
        ...prev,
        tipo: value,
        opciones:
          prev.opciones.length >= 2
            ? prev.opciones
            : [
                { respuesta: "", correcta: false },
                { respuesta: "", correcta: false },
              ],
      }));
      return;
    }

    if (name === "pregunta") {
      const blanks = (value.match(/_____/g) || []).length;
      setPreguntaForm((prev) => {
        if (prev.tipo !== "autocompletar") return { ...prev, pregunta: value };
        const respuestas = [...prev.respuestasAutocompletar];
        while (respuestas.length < blanks) respuestas.push("");
        return {
          ...prev,
          pregunta: value,
          respuestasAutocompletar: respuestas.slice(0, blanks || 1),
        };
      });
      return;
    }

    setPreguntaForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleOpcionPreguntaChange = (index, field, value) => {
    setPreguntaForm((prev) => {
      const updated = [...prev.opciones];
      updated[index][field] = value;
      if (prev.tipo === "única" && field === "correcta" && value) {
        updated.forEach((op, i) => {
          if (i !== index) op.correcta = false;
        });
      }
      return { ...prev, opciones: updated };
    });
  };

  const handleAgregarOpcion = () => {
    setPreguntaForm((prev) => ({
      ...prev,
      opciones: [...prev.opciones, { respuesta: "", correcta: false }],
    }));
  };

  const handleEliminarOpcion = (index) => {
    setPreguntaForm((prev) => {
      if (prev.opciones.length <= 2) return prev;
      const updated = prev.opciones.filter((_, i) => i !== index);
      return { ...prev, opciones: updated };
    });
  };

  const validarPreguntaActual = () => {
    if (!preguntaForm.pregunta.trim()) {
      showAlert("Error", "La pregunta no puede estar vacía.", "error");
      return false;
    }
    if (preguntaForm.rango[0] >= preguntaForm.rango[1]) {
      showAlert("Error", "El rango debe tener un mínimo menor al máximo.", "error");
      return false;
    }
    if (preguntaForm.tipo === "autocompletar") {
      const blanks = (preguntaForm.pregunta.match(/_____/g) || []).length;
      if (blanks === 0) {
        showAlert("Error", "Para autocompletar debes incluir al menos un espacio (_____).", "error");
        return false;
      }
      if (preguntaForm.respuestasAutocompletar.some((r) => !r.trim())) {
        showAlert("Error", "Completa todas las respuestas de autocompletar.", "error");
        return false;
      }
      return true;
    }
    if (preguntaForm.tipo === "única" || preguntaForm.tipo === "multiple") {
      if (preguntaForm.opciones.length < 2) {
        showAlert("Error", "Debe haber al menos 2 opciones.", "error");
        return false;
      }
      if (preguntaForm.opciones.some((op) => !op.respuesta.trim())) {
        showAlert("Error", "Ninguna opción puede estar vacía.", "error");
        return false;
      }
      const correctas = preguntaForm.opciones.filter((op) => op.correcta).length;
      if (preguntaForm.tipo === "única" && correctas !== 1) {
        showAlert("Error", "Debe haber exactamente una opción correcta.", "error");
        return false;
      }
      if (preguntaForm.tipo === "multiple" && correctas < 1) {
        showAlert("Error", "Debe haber al menos una opción correcta.", "error");
        return false;
      }
    }
    return true;
  };

  const buildOpciones = () => {
    if (preguntaForm.tipo === "falso_verdadero") {
      return [
        { respuesta: "Verdadero", correcta: preguntaForm.valorVerdaderoFalso === true },
        { respuesta: "Falso", correcta: preguntaForm.valorVerdaderoFalso === false },
      ];
    }
    if (preguntaForm.tipo === "autocompletar") {
      return preguntaForm.respuestasAutocompletar.map((respuesta) => ({
        respuesta: respuesta.trim(),
        correcta: true,
      }));
    }
    return preguntaForm.opciones.map((op) => ({ respuesta: op.respuesta.trim(), correcta: !!op.correcta }));
  };

  const handleAgregarPregunta = () => {
    if (!validarPreguntaActual()) return;
    const payload = {
      pregunta: preguntaForm.pregunta.trim(),
      descripcion: preguntaForm.descripcion.trim(),
      tipo: preguntaForm.tipo,
      rango: preguntaForm.rango,
      estado: 1,
      opciones: buildOpciones(),
    };
    if (isSingleMode) {
      handleGuardarPreguntaIndividual(payload);
      return;
    }
    setPreguntasCurso((prev) => [
      ...prev,
      payload,
    ]);
    resetPreguntaForm();
  };

  const handleGuardarPreguntaIndividual = async (payload) => {
    if (!idCurso) return;
    try {
      setLoadingGuardar(true);
      const fullPayload = { ...payload, idCurso: Number(idCurso) };
      if (initialPregunta?.idPregunta) {
        await updatePregunta(initialPregunta.idPregunta, fullPayload);
      } else {
        await createPregunta(fullPayload);
      }
      showAlert("Guardado", "Pregunta guardada correctamente.", "success");
      onSuccess?.();
      if (!initialPregunta?.idPregunta) resetPreguntaForm();
    } catch (error) {
      console.error("Error al guardar pregunta:", error);
      showAlert(
        "Error",
        error?.response?.data?.message || error?.message || "No se pudo guardar la pregunta.",
        "error"
      );
    } finally {
      setLoadingGuardar(false);
    }
  };

  const handleGuardarPreguntas = async () => {
    if (!idCurso) return;
    if (preguntasCurso.length === 0) {
      showAlert("Aviso", "Debes agregar al menos una pregunta.", "info");
      return;
    }
    try {
      setLoadingGuardar(true);
      await Promise.all(
        preguntasCurso.map((pregunta) =>
          createPregunta({
            ...pregunta,
            idCurso: Number(idCurso),
          })
        )
      );
      showAlert("Listo", "Preguntas guardadas correctamente.", "success");
      setPreguntasCurso([]);
      resetPreguntaForm();
      onSuccess?.();
    } catch (error) {
      console.error("Error al guardar preguntas:", error);
      showAlert(
        "Error",
        error?.response?.data?.message || error?.message || "No se pudieron guardar.",
        "error"
      );
    } finally {
      setLoadingGuardar(false);
    }
  };

  return (
    <Card sx={{ backgroundColor: "#f8f9fa", borderRadius: 2, boxShadow: "none", width: "100%" }}>
      <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
        {!isSingleMode && (
          <Box display="flex" alignItems="center" mb={2}>
            <QuizIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6">Agregar preguntas a {nombreCurso || `curso ${idCurso}`}</Typography>
          </Box>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth select name="tipo" label="Tipo de pregunta" value={preguntaForm.tipo} onChange={handlePreguntaFieldChange}>
              <MenuItem value="multiple">Selección múltiple</MenuItem>
              <MenuItem value="única">Una sola respuesta</MenuItem>
              <MenuItem value="falso_verdadero">Verdadero / Falso</MenuItem>
              <MenuItem value="autocompletar">Autocompletar</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Descripción (opcional)" name="descripcion" value={preguntaForm.descripcion} onChange={handlePreguntaFieldChange} />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Pregunta"
              name="pregunta"
              value={preguntaForm.pregunta}
              onChange={handlePreguntaFieldChange}
            />
          </Grid>

          <Grid item xs={12}>
            <FormLabel sx={{ mb: 1, display: "block" }}>Rango de edad permitido</FormLabel>
            <Slider
              value={preguntaForm.rango}
              onChange={(_, newValue) => setPreguntaForm((prev) => ({ ...prev, rango: newValue }))}
              valueLabelDisplay="auto"
              min={0}
              max={100}
              step={1}
              disableSwap
            />
          </Grid>

          {(preguntaForm.tipo === "única" || preguntaForm.tipo === "multiple") &&
            preguntaForm.opciones.map((opcion, index) => (
              <Grid item xs={12} key={`op-${index}`}>
                <Box display="flex" alignItems={{ xs: "stretch", sm: "center" }} gap={1} flexWrap={{ xs: "wrap", sm: "nowrap" }}>
                  {preguntaForm.tipo === "única" ? (
                    <Radio checked={opcion.correcta} onChange={() => handleOpcionPreguntaChange(index, "correcta", true)} />
                  ) : (
                    <Checkbox checked={opcion.correcta} onChange={(e) => handleOpcionPreguntaChange(index, "correcta", e.target.checked)} />
                  )}
                  <TextField
                    fullWidth
                    label={`Opción ${index + 1}`}
                    value={opcion.respuesta}
                    onChange={(e) => handleOpcionPreguntaChange(index, "respuesta", e.target.value)}
                  />
                  <IconButton
                    aria-label={`Eliminar opción ${index + 1}`}
                    onClick={() => handleEliminarOpcion(index)}
                    disabled={preguntaForm.opciones.length <= 2}
                    sx={{ alignSelf: { xs: "flex-end", sm: "center" } }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </Grid>
            ))}

          {(preguntaForm.tipo === "única" || preguntaForm.tipo === "multiple") && (
            <Grid item xs={12}>
              <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAgregarOpcion}>
                Agregar opción
              </Button>
            </Grid>
          )}

          {preguntaForm.tipo === "falso_verdadero" && (
            <Grid item xs={12}>
              <Box display="flex" alignItems="center" gap={1}>
                <FormLabel>Respuesta correcta:</FormLabel>
                <Switch
                  checked={preguntaForm.valorVerdaderoFalso}
                  onChange={(e) => setPreguntaForm((prev) => ({ ...prev, valorVerdaderoFalso: e.target.checked }))}
                />
                <Typography variant="body2">{preguntaForm.valorVerdaderoFalso ? "Verdadero" : "Falso"}</Typography>
              </Box>
            </Grid>
          )}

          {preguntaForm.tipo === "autocompletar" &&
            preguntaForm.respuestasAutocompletar.map((respuesta, index) => (
              <Grid item xs={12} key={`auto-${index}`}>
                <TextField
                  fullWidth
                  label={`Respuesta ${index + 1}`}
                  value={respuesta}
                  onChange={(e) =>
                    setPreguntaForm((prev) => {
                      const arr = [...prev.respuestasAutocompletar];
                      arr[index] = e.target.value;
                      return { ...prev, respuestasAutocompletar: arr };
                    })
                  }
                />
              </Grid>
            ))}

          <Grid item xs={12} display="flex" gap={1} flexWrap="wrap">
            <Button variant="contained" onClick={handleAgregarPregunta} disabled={loadingGuardar} fullWidth={isSingleMode}>
              {loadingGuardar ? "Guardando..." : isSingleMode ? "Guardar pregunta" : "Agregar pregunta"}
            </Button>
            <Button variant="outlined" onClick={resetPreguntaForm} fullWidth={isSingleMode}>
              Limpiar pregunta
            </Button>
            {isSingleMode && onCancel && (
              <Button variant="text" onClick={onCancel} fullWidth>
                Cancelar
              </Button>
            )}
          </Grid>
        </Grid>

        {!isSingleMode && preguntasCurso.length > 0 && (
          <List dense sx={{ bgcolor: "#fafafa", borderRadius: 1, mt: 2, mb: 2 }}>
            {preguntasCurso.map((item, idx) => (
              <ListItem
                key={`preg-${idx}`}
                secondaryAction={
                  <IconButton edge="end" onClick={() => setPreguntasCurso((prev) => prev.filter((_, i) => i !== idx))}>
                    <DeleteIcon />
                  </IconButton>
                }
              >
                <ListItemText primary={`${idx + 1}. ${item.pregunta}`} secondary={`Tipo: ${item.tipo}`} />
              </ListItem>
            ))}
          </List>
        )}

        {!isSingleMode && (
          <Box display="flex" justifyContent="flex-end">
            <Button variant="contained" onClick={handleGuardarPreguntas} disabled={loadingGuardar || preguntasCurso.length === 0}>
              {loadingGuardar ? "Guardando..." : "Guardar preguntas del curso"}
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default AddCourseQuestionsForm;
